import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Field } from '@atlaskit/form';
import { AsyncCreatableSelect } from '@atlaskit/select';
import { B300, G300 } from '@atlaskit/theme/colors';
import {
  SanitizedFormattedHtmlMessage,
  SanitizedFormattedHtmlMessageProps,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import { catalog } from '@atlassiansox/metal-client';
import styled from '@emotion/styled';
import debounce from 'lodash.debounce';
import React, { useEffect, useState } from 'react';
import { metalClient } from '../../../../metrics';
import { useSimpleSignupState } from '../../hook';
import { messages } from '../../messages';
import { buildSimpleSignupProduct, getSiteDomain } from '../../utility';
import { FieldType } from '../model';
import { ErrorMessage, FieldWrapper, fontCharlieText } from '../styled';
import { IconValidation } from './icon-validation';
import { ValidationStateType } from '../../types';
import {
  useAppDispatch,
  useAppSelector,
  updateLoggedInFormValid,
  updateLoggedInFormDataProducts,
  updateLoggedInFormDataFreeBreach,
  updateLoggedInFormInput
} from '../../../../redux';

type OptionsType = {
  readonly value: string;
  readonly label: string;
};

function createOption(value: string, domain: string) {
  return {
    label: value + domain,
    value
  };
}

export function InputSitenameExisting() {
  // TODO: there should be some analytics but im not seeing any in the old form
  // besides on the switch betweeen the dropdown and input for sites

  const { intl, userInfo } = useSimpleSignupState();
  const dispatch = useAppDispatch();
  const { bbWorkspaceName, cloudName } = useAppSelector(
    ({ loggedInFormData }) => loggedInFormData
  );
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const stargateClient = useStargateClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [newOption, setNewOption] = useState<OptionsType>();
  const [inputValue, setInputValue] = useState<OptionsType>();
  const [validationState, setValidationState] = useState<ValidationStateType>();
  const [validationMessage, setValidationMessage] = useState<
    undefined | SanitizedFormattedHtmlMessageProps
  >();
  const validSitename = /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/;
  const sites = userInfo?.sites || [];
  const siteDomain = getSiteDomain();
  const sitesMap = new Map(sites.map((obj) => [obj.displayName, obj]));

  useEffect(() => {
    setOptions(
      // @ts-ignore
      [...sitesMap.keys()].map((displayName) => {
        return createOption(displayName, siteDomain);
      })
    );
  }, [userInfo]);

  useEffect(() => {
    // no selection has been made, or a new site was just added to options
    if (!cloudName) {
      setInputValue(options[0]);
      return;
    }

    // selection has been made, and may or may not be included in options
    if (cloudName && options) {
      const targetOption = options.find((o) => o.value === cloudName);
      if (targetOption) {
        // already included in options, existing site
        setNewOption(undefined);
        setInputValue(targetOption);
      } else {
        // not included in options, new site
        const createdOption = createOption(cloudName, siteDomain);
        setNewOption(createdOption);
        setInputValue(createdOption);
      }
    }
  }, [options, cloudName]);

  // set input value in SimpleSignupState whenever it changes
  useEffect(() => {
    if (!inputValue) {
      dispatch(updateLoggedInFormValid(false));
      return;
    }

    const { value: cloudName } = inputValue;
    const selectedSite = sitesMap.get(cloudName);
    let breachStatus = false;
    const existingWorkspace = !!bbWorkspaceName;
    if (selectedSite && selectedSite.products) {
      try {
        void Promise.all(
          selectedSite.products.map(async (product) => {
            const status = await stargateClient.postExpandFreeValidation({
              cloudId: selectedSite?.cloudId,
              productKeys: [product]
            });
            return status.isInBreachOfFree;
          })
        )
          .then((res) => {
            breachStatus = res.includes(true);
            dispatch(updateLoggedInFormDataFreeBreach(breachStatus));
            const { productsNew, productsExisting } = buildSimpleSignupProduct({
              cloudName,
              selectedSite,
              breachStatus,
              existingWorkspace
            });
            dispatch(
              updateLoggedInFormDataProducts({
                cloudName,
                productsNew,
                productsExisting
              })
            );
            dispatch(updateLoggedInFormValid(true));
          })
          .catch((err) => {
            // in the event that Promise all fails, fail gracefully and continue
            createAnalyticsEvent({
              eventName: 'bxp.formCloudSignup.freeBreachStatusRequestFailure',
              eventComponent: 'form',
              event: 'request',
              data: {
                error: err
              }
            }).fire();
            metalClient.error.submit({
              name: catalog.error.REQUEST,
              page: 'sign-up-layout-simple-signup'
            });
            const { productsNew, productsExisting } = buildSimpleSignupProduct({
              cloudName,
              selectedSite,
              breachStatus,
              existingWorkspace
            });

            dispatch(
              updateLoggedInFormDataProducts({
                cloudName,
                productsNew,
                productsExisting
              })
            );
            dispatch(updateLoggedInFormValid(true));
          });
      } catch (err) {
        // in the event that the try fails, fail gracefully and continue
        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.freeBreachStatusRequestFailure',
          eventComponent: 'form',
          event: 'request',
          data: {
            error: err
          }
        }).fire();
        metalClient.error.submit({
          name: catalog.error.REQUEST,
          page: 'sign-up-layout-simple-signup'
        });
        const { productsNew, productsExisting } = buildSimpleSignupProduct({
          cloudName,
          selectedSite,
          breachStatus,
          existingWorkspace
        });

        dispatch(
          updateLoggedInFormDataProducts({
            cloudName,
            productsNew,
            productsExisting
          })
        );
        dispatch(updateLoggedInFormValid(true));
      }
    } else {
      const { productsNew, productsExisting } = buildSimpleSignupProduct({
        cloudName,
        selectedSite,
        breachStatus,
        existingWorkspace
      });

      dispatch(
        updateLoggedInFormDataProducts({
          cloudName,
          productsNew,
          productsExisting
        })
      );
      dispatch(updateLoggedInFormValid(true));
    }
  }, [inputValue]);

  // user selects 'Create ...'
  function handleCreateOption(value: string) {
    const option = createOption(value, siteDomain);
    setNewOption(option);
    setInputValue(option);
  }

  // user types into input
  async function loadOptions(
    value: string,
    callback: (options: OptionsType[]) => void
  ) {
    setInputValue(undefined);
    const validState = await validateSitename(value);
    if (validState === 'error') {
      setIsMenuOpen(false);
    } else {
      setIsMenuOpen(true);
    }
    setValidationState(validState);
    // filter out options that do not match what's currently typed,
    // otherwise the 'Create ...' prompt lands at the bottom of the list
    callback(
      options.filter((option) =>
        option.label.toLowerCase().includes(value.toLowerCase())
      )
    );
  }

  // user selects from the dropdown
  function handleChange(value: OptionsType) {
    setInputValue(value);
  }

  function handleBlur() {
    setValidationMessage(undefined);
    setValidationState(undefined);
  }

  function handleMenuOpen() {
    if (validationState !== 'error') {
      setIsMenuOpen(true);
    }
  }

  function handleMenuClose() {
    setIsMenuOpen(false);
  }

  function validateSitename(value: string): Promise<ValidationStateType> {
    return new Promise(async (resolve) => {
      setValidationMessage(undefined);
      if (value.length === 0) {
        resolve(undefined);
      }
      if (
        !validSitename.test(value) ||
        (value.length > 0 && value.length < 3)
      ) {
        setValidationMessage(messages.errorSitenameInvalid);
        resolve('error');
      }
      if (options.map((p) => p.value).includes(value)) {
        resolve('success');
      }

      setValidationState('validating');
      const siteNameCheck = await stargateClient.checkCloudNameAvailability({
        cloudName: value,
        webPlatform: 'magnolia'
      });

      if (siteNameCheck?.availability !== 'AVAILABLE') {
        setValidationMessage(messages.errorSitenameUnavailable);
        resolve('error');
      } else {
        resolve('success');
      }

      resolve(undefined);
    });
  }
  const handleInputChange = (value: string) => {
    if (value === '') {
      setValidationMessage(undefined);
      setValidationState(undefined);
    }
  };

  return (
    <>
      <Field
        name="sitenameExisting"
        label={intl.formatMessage(messages.inputLabelSitenameExisting)}
        isRequired
      >
        {({ fieldProps }: FieldType) => (
          <FieldWrapper>
            {validationMessage && (
              <ErrorMessage className="location-bottom">
                <SanitizedFormattedHtmlMessage {...validationMessage} />
              </ErrorMessage>
            )}
            <div data-testid="sitenameExisting">
              <StyledAsyncCreatableSelect
                {...fieldProps}
                loadOptions={debounce(loadOptions, 1000)}
                defaultOptions={newOption ? [newOption, ...options] : options}
                allowCreateWhileLoading={false}
                isClearable={false}
                validationState={validationState}
                placeholder={''}
                onCreateOption={handleCreateOption}
                onChange={handleChange}
                onBlur={handleBlur}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                menuIsOpen={isMenuOpen}
                value={inputValue}
                onInputChange={handleInputChange}
                elemAfterInput={
                  <IconValidation validationState={validationState} />
                }
              />
            </div>
          </FieldWrapper>
        )}
      </Field>

      <SitenameToggleWrapper
        onClick={() => {
          dispatch(updateLoggedInFormValid(false));
          dispatch(updateLoggedInFormInput(true));
        }}
      >
        <SanitizedFormattedHtmlMessage {...messages.inputToggleSitenameNew} />
      </SitenameToggleWrapper>
    </>
  );
}

const StyledAsyncCreatableSelect = styled(AsyncCreatableSelect)`
  font-size: 14px;
  > div {
    ${(props) => {
      if (props.validationState === 'success') {
        return ` border-color:${G300} !important`; // green
      }
      return '';
    }};
  }
  svg {
    margin-bottom: 0px;
  }
`;
const SitenameToggleWrapper = styled.div`
  font-family: ${fontCharlieText};
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: ${B300};
  vertical-align: middle;
  margin: 2px 0 16px 0;
  font-weight: 500;
  cursor: pointer;
`;
